/**
 * Social proof — a clay stats band plus a few warm testimonials. Numbers use
 * tabular figures so the band stays visually steady.
 */
import { Quote, Star } from 'lucide-react';

const STATS = [
  { value: '1,200+', label: 'Happy pet parents' },
  { value: '350+', label: 'Verified vendors' },
  { value: '12k', label: 'Bookings made' },
  { value: '4.9★', label: 'Average rating' },
];

const TESTIMONIALS = [
  {
    quote:
      'Booked a last-minute vet visit in under a minute. Bella was seen the same afternoon — total lifesaver.',
    name: 'Aarti S.',
    role: 'Mom to Bella 🐕',
    tint: '#FF4D8A',
  },
  {
    quote:
      'As a groomer, my weekends used to be chaos. Now my calendar fills itself and no-shows are way down.',
    name: 'Paws & Bubbles',
    role: 'Grooming studio',
    tint: '#2563EB',
  },
  {
    quote:
      'Everything — walks, meds, training — finally lives in one place. It feels made for pet people.',
    name: 'Rohan & Coco',
    role: 'Pet parents',
    tint: '#2E7D32',
  },
];

export function LandingSocialProof() {
  return (
    <section id="social-proof" className="scroll-mt-24 bg-secondary/40 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-5">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {STATS.map((s) => (
            <div
              key={s.label}
              className="rounded-card border-2 border-brand-outline-variant/20 bg-card p-6 text-center shadow-clay-sm"
            >
              <p className="font-heading text-3xl font-extrabold tracking-tight tabular-nums text-primary sm:text-4xl">
                {s.value}
              </p>
              <p className="mt-1 text-sm font-medium text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="mt-12 text-center">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            Loved by pets &amp; their people
          </h2>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <figure
              key={t.name}
              className="flex flex-col rounded-card border-2 border-brand-outline-variant/20 bg-card p-6 shadow-clay"
            >
              <Quote className="h-7 w-7 text-primary/40" />
              <blockquote className="mt-3 flex-1 text-foreground">{t.quote}</blockquote>
              <div className="mt-5 flex items-center gap-3">
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-full font-heading text-base font-bold text-white shadow-clay-sm"
                  style={{ backgroundColor: t.tint }}
                >
                  {t.name.charAt(0)}
                </span>
                <figcaption>
                  <p className="font-heading text-sm font-bold leading-tight">{t.name}</p>
                  <p className="text-sm text-muted-foreground">{t.role}</p>
                </figcaption>
                <div className="ml-auto flex">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-warning text-warning" />
                  ))}
                </div>
              </div>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
